<template>
	<div>

		<!-- ── STATUS CHIPS (último estado de cada cámara en la página actual) ── -->
		<div v-if="camera_chips.length" class="px-2 pt-2 pb-1 d-flex flex-wrap align-center">
			<span class="caption grey--text text--darken-1 mr-2">Estado:</span>
			<v-chip
				v-for="cam in camera_chips"
				:key="cam.deviceId"
				x-small
				class="mr-1 mb-1"
				:color="cam.chip_color"
				:outlined="info_search.deviceId !== cam.deviceId"
				dark
				style="cursor:pointer"
				@click="filter_by_camera(cam)"
			>
				<v-icon x-small left>mdi-cctv</v-icon>
				{{ cam.name }}
			</v-chip>
			<v-btn v-if="has_active_filters" x-small text class="ml-1 mb-1" @click="clear_filters">
				<v-icon x-small left>mdi-close</v-icon>
				Limpiar
			</v-btn>
		</div>

		<!-- ── TOOLBAR DE FILTROS ── -->
		<v-toolbar dense flat style="border-bottom:1px solid rgba(0,0,0,0.12)">
			<v-btn x-small text @click="show_filters = !show_filters">
				<v-icon x-small left>{{ show_filters ? 'mdi-filter-off' : 'mdi-filter' }}</v-icon>
				Filtros
				<v-chip v-if="active_filter_count > 0" x-small color="primary" class="ml-1">{{ active_filter_count }}</v-chip>
			</v-btn>
			<v-divider vertical class="mx-2"/>
			<v-btn x-small text @click="group_duplicates = !group_duplicates">
				<v-icon x-small left>{{ group_duplicates ? 'mdi-layers' : 'mdi-layers-off' }}</v-icon>
				{{ group_duplicates ? 'Agrupados' : 'Ver todos' }}
			</v-btn>
			<v-spacer/>
			<span v-if="has_active_filters" class="caption primary--text mr-2">
				{{ list_render.length }} visible / {{ total }} total
			</span>
		</v-toolbar>

		<!-- ── PANEL DE FILTROS ── -->
		<v-expand-transition>
			<div v-show="show_filters" class="pa-2 d-flex flex-wrap align-end grey lighten-5">
				<v-select
					v-model="filter_device"
					:items="list_devices"
					item-text="name"
					item-value="deviceId"
					label="Cámara"
					dense outlined hide-details clearable
					class="mr-2 mb-2"
					style="max-width:200px"
				/>
				<v-select
					v-model="filter_module"
					:items="module_options"
					label="Módulo"
					dense outlined hide-details clearable
					class="mr-2 mb-2"
					style="max-width:160px"
				/>
				<v-select
					v-model="filter_state"
					:items="state_options"
					item-text="text"
					item-value="value"
					label="Estado"
					dense outlined hide-details clearable
					class="mr-2 mb-2"
					style="max-width:160px"
				/>
				<v-btn small color="primary" class="mb-2 mr-2" @click="apply_filters">
					<v-icon small left>mdi-magnify</v-icon>
					Buscar
				</v-btn>
				<v-btn small text class="mb-2" @click="clear_filters">
					Limpiar
				</v-btn>
			</div>
		</v-expand-transition>

		<!-- ── TABLA PRINCIPAL ── -->
		<v-data-table
			dense
			fixed-header
			:headers="headers"
			:items="list_render"
			class="elevation-5"
			:hide-default-footer="height < 56 ? true : false"
			:options.sync="options"
			:server-items-length="total"
			loading-text="Loading... Please wait"
			:height="height + 'px'"
			item-key="index"
			:loading="loading"
			:footer-props="{
				showFirstLastPage  : true,
				itemsPerPageOptions: [15, 30],
				showCurrentPage    : true
			}"
		>
			<!-- slot original oculto — mantiene lógica del watch intacta -->
			<template v-slot:top>
				<v-row v-show="false">
					<template v-for="(item_search) of search_options">
						<v-col cols="3" :key="item_search.key">
							<searcher
								v-if="item_search.type == 'select_searcher'"
								:info_to_component="item_search"
								:info_to_list="render_listed(item_search.list)"
								@receive_info="info_search[item_search.key] = $event"
							/>
						</v-col>
					</template>
				</v-row>
			</template>

			<!-- Cámara — clic abre drawer de historial -->
			<template v-slot:item.deviceId="{ item }">
				<span
					class="primary--text"
					style="cursor:pointer;white-space:nowrap"
					@click="open_drawer(item)"
				>
					<v-icon x-small color="primary" class="mr-1">mdi-history</v-icon>
					{{ item.device || item.deviceId }}
				</span>
			</template>

			<!-- Estado — chip con color -->
			<template v-slot:item.state="{ item }">
				<v-chip x-small :color="state_color(item.state)" dark>{{ item.state }}</v-chip>
			</template>

			<!-- Código — con badge de repeticiones -->
			<template v-slot:item.code="{ item }">
				<span>{{ item.code }}</span>
				<v-chip v-if="item._count > 1" x-small color="grey lighten-1" class="ml-1">
					×{{ item._count }}
				</v-chip>
			</template>

		</v-data-table>

		<!-- ── DRAWER DE HISTORIAL POR CÁMARA ── -->
		<v-navigation-drawer
			v-model="drawer_open"
			fixed right temporary
			width="380"
		>
			<!-- Encabezado -->
			<v-list-item class="primary" dark>
				<v-list-item-icon class="mr-2 my-auto">
					<v-icon>mdi-cctv</v-icon>
				</v-list-item-icon>
				<v-list-item-content>
					<v-list-item-title class="body-2 font-weight-bold">
						{{ drawer_camera ? drawer_camera.name : '' }}
					</v-list-item-title>
					<v-list-item-subtitle class="caption">
						Historial · {{ drawer_total }} registros
					</v-list-item-subtitle>
				</v-list-item-content>
				<v-list-item-action>
					<v-btn icon dark small @click="filter_by_camera(drawer_camera)" title="Filtrar en tabla">
						<v-icon small>mdi-table-search</v-icon>
					</v-btn>
				</v-list-item-action>
				<v-list-item-action>
					<v-btn icon dark small @click="drawer_open = false">
						<v-icon small>mdi-close</v-icon>
					</v-btn>
				</v-list-item-action>
			</v-list-item>
			<v-divider/>

			<!-- Cargando primer lote -->
			<div v-if="drawer_loading && drawer_list.length === 0" class="text-center pa-6">
				<v-progress-circular indeterminate color="primary" size="32"/>
			</div>

			<!-- Sin registros -->
			<div v-else-if="!drawer_loading && drawer_list.length === 0" class="text-center pa-6 caption grey--text">
				Sin registros para esta cámara
			</div>

			<!-- Lista de eventos -->
			<v-list v-else dense class="pa-0">
				<template v-for="(ev, i) in drawer_list">
					<v-list-item :key="ev.id || i" class="py-1">
						<v-list-item-icon class="mr-2 my-auto">
							<v-icon small :color="state_color(ev.state)">mdi-circle</v-icon>
						</v-list-item-icon>
						<v-list-item-content>
							<v-list-item-title class="caption font-weight-medium">
								{{ ev.code }}
							</v-list-item-title>
							<v-list-item-subtitle class="caption">
								{{ ev.module }} · {{ ev.date || ev.createdAt }}
							</v-list-item-subtitle>
							<v-list-item-subtitle v-if="ev.detail" class="caption grey--text">
								{{ ev.detail }}
							</v-list-item-subtitle>
						</v-list-item-content>
						<v-list-item-action class="my-auto">
							<v-chip x-small :color="state_color(ev.state)" dark>{{ ev.state }}</v-chip>
						</v-list-item-action>
					</v-list-item>
					<v-divider :key="'d' + i"/>
				</template>
			</v-list>

			<!-- Cargando más -->
			<v-progress-linear v-if="drawer_loading && drawer_list.length > 0" indeterminate color="primary" height="2"/>

			<!-- Ver más -->
			<div v-if="!drawer_loading && drawer_list.length > 0 && drawer_list.length < drawer_total" class="text-center pa-2">
				<v-btn small text color="primary" @click="load_more_drawer">
					Ver más ({{ drawer_total - drawer_list.length }} restantes)
				</v-btn>
			</div>

		</v-navigation-drawer>

	</div>
</template>

<script>
	import searcher from "@/components/info/searcher.vue"

	export default {
		name      : "technical",
		components: { searcher },

		data: (vm) => ({
			info_search : {},
			headers     : [
				{ text: vm.$ml.get("code"),   value: "code"     },
				{ text: vm.$ml.get("module"), value: "module"   },
				{ text: vm.$ml.get("device"), value: "deviceId" },
				{ text: vm.$ml.get("detail"), value: "detail"   },
				{ text: vm.$ml.get("type"),   value: "type"     },
				{ text: vm.$ml.get("state"),  value: "state"    },
				{ text: vm.$ml.get("date"),   value: "date"     },
			],
			height  : 400,
			options : { itemsPerPage: 30 },
			total   : 0,
			loading : false,
			list    : [],
			search_options: [
				{
					key         : 'deviceId',
					text        : 'search',
					placeholder : 'device',
					list        : 'list_devices',
					key_list    : 'deviceId',
					text_list   : 'name',
					type        : 'select_searcher',
					key_search  : 'name',
					key_search_1: 'deviceId',
					visible     : true,
				},
			],
			list_devices: [],

			// Filtros
			show_filters  : false,
			filter_device : null,
			filter_module : null,
			filter_state  : null,
			group_duplicates: true,
			module_options: ['ping', 'retransmission', 'retransmission_cloud', 'record', 'rtsp'],
			state_options : [
				{ text: 'OK (0)',           value: '0'  },
				{ text: 'Reconectando (-1)', value: '-1' },
				{ text: 'Detenido (2)',      value: '2'  },
				{ text: 'Advertencia (3)',   value: '3'  },
				{ text: 'Error (4)',         value: '4'  },
			],

			// Drawer
			drawer_open   : false,
			drawer_camera : null,
			drawer_list   : [],
			drawer_loading: false,
			drawer_page   : 1,
			drawer_total  : 0,
		}),

		watch: {
			options: {
				handler() {
					if (this.info_search && (this.info_search.code || this.info_search.module || this.info_search.deviceId)) {
						this.get_search();
					} else {
						this.get_list();
					}
				},
				deep: true,
			},
		},

		created() {
			this.$Format.update_list(this.$ml, 'ml');
			const temp_devices = this.$Helper.get_local('list_devices');
			if (temp_devices) this.list_devices = temp_devices;
		},

		computed: {
			list_render() {
				// Filtro de estado client-side
				const base = (this.filter_state !== null && this.filter_state !== '')
					? this.list.filter(item => String(item.state) === String(this.filter_state))
					: this.list;

				if (!this.group_duplicates) return base;

				// Deduplicación: eventos consecutivos igual cámara+código en < 60 s → una fila con ×N
				const result = [];
				base.forEach(item => {
					const last = result[result.length - 1];
					if (
						last &&
						last.deviceId === item.deviceId &&
						last.code     === item.code     &&
						Math.abs(new Date(item.createdAt) - new Date(last.createdAt)) < 60000
					) {
						last._count = (last._count || 1) + 1;
					} else {
						result.push({ ...item, _count: 1 });
					}
				});
				return result;
			},

			// Chip por cámara con su peor estado registrado en la página actual
			camera_chips() {
				const map = {};
				this.list.forEach(item => {
					if (!item.deviceId) return;
					const did = item.deviceId;
					if (!map[did]) {
						map[did] = { deviceId: did, name: item.device || did, worst_state: parseInt(item.state) || 0 };
					} else {
						const s = parseInt(item.state) || 0;
						if (this.state_severity(s) > this.state_severity(map[did].worst_state)) {
							map[did].worst_state = s;
						}
					}
				});
				return Object.values(map)
					.map(c => ({ ...c, chip_color: this.state_color(c.worst_state) }))
					.sort((a, b) => this.state_severity(b.worst_state) - this.state_severity(a.worst_state));
			},

			has_active_filters() {
				return !!(this.filter_device || this.filter_module || (this.filter_state !== null && this.filter_state !== ''));
			},

			active_filter_count() {
				return [this.filter_device, this.filter_module, this.filter_state].filter(v => v !== null && v !== '').length;
			},
		},

		methods: {
			// ── Carga de datos ──────────────────────────────────────────────────

			get_list() {
				this.list    = [];
				this.loading = true;
				const { page, itemsPerPage } = this.options;
				let pg = itemsPerPage == 15
					? (page % 2 == 0 ? page / 2 : parseInt(page / 2) + 1)
					: page;
				const api = this.$URL + "/api/v1/technical_records/?page=" + pg;
				this.$axios.get(api).then(res => {
					if (res && res.data) {
						const items = this.render_format(res.data.rows);
						this.total  = res.data.count;
						if (items) this.list = items;
					}
					this.loading = false;
				}).catch(er => {
					console.log("Error get technical:", er);
					this.loading = false;
				});
			},

			get_search() {
				const { page, itemsPerPage } = this.options;
				let pg = itemsPerPage == 15
					? (page % 2 == 0 ? page / 2 : parseInt(page / 2) + 1)
					: page;
				const api    = this.$URL + "/api/v1/technical_records/find/?page=" + pg;
				this.loading = true;
				this.$axios.post(api, this.info_search).then(res => {
					if (res && res.data) {
						const items = this.render_format(res.data.rows);
						this.total  = res.data.count;
						if (items) this.list = items;
					}
					this.loading = false;
				}).catch(er => {
					console.log("Error get technical search:", er);
					this.loading = false;
				});
			},

			render_format(items) {
				let list_temp = [...items];
				if (list_temp && list_temp.length > 0) {
					list_temp.forEach(dev => {
						dev.device = this.$ml.get('data_no_found');
						if (dev.deviceId) {
							const temp_dev = this.$Format.render_device(dev.deviceId);
							if (temp_dev) dev.device = temp_dev.name;
						}
						if (dev.createdAt) {
							const temp_date = this.$Format.format_date(dev.createdAt, "createdAt");
							if (temp_date) dev.date = temp_date;
						}
						dev.color_tag = this.state_color(dev.state);
					});
					return list_temp;
				}
				return false;
			},

			render_listed(item) {
				if (item && this[item]) return this[item];
			},

			// ── Helpers de color/severidad ──────────────────────────────────────

			state_color(state) {
				const s = parseInt(state);
				if (s === 0)  return 'success';
				if (s === -1) return 'primary';
				if (s === 2)  return 'secondary';
				if (s === 3)  return 'warning';
				if (s >= 4)   return 'error';
				return 'secondary';
			},

			state_severity(state) {
				const s = parseInt(state);
				if (s >= 4)   return 4;
				if (s === 3)  return 3;
				if (s === -1) return 2;
				if (s === 0)  return 1;
				return 0;
			},

			// ── Filtros ─────────────────────────────────────────────────────────

			filter_by_camera(cam) {
				if (!cam) return;
				this.filter_device    = cam.deviceId;
				this.info_search      = { deviceId: cam.deviceId };
				this.get_search();
			},

			apply_filters() {
				this.info_search = {};
				if (this.filter_device) this.info_search.deviceId = this.filter_device;
				if (this.filter_module) this.info_search.module   = this.filter_module;
				if (this.info_search.deviceId || this.info_search.module) {
					this.get_search();
				} else {
					this.get_list();
				}
			},

			clear_filters() {
				this.info_search  = {};
				this.filter_device = null;
				this.filter_module = null;
				this.filter_state  = null;
				this.get_list();
			},

			// ── Drawer de historial ─────────────────────────────────────────────

			open_drawer(item) {
				if (!item || !item.deviceId) return;
				this.drawer_camera  = { deviceId: item.deviceId, name: item.device || item.deviceId };
				this.drawer_list    = [];
				this.drawer_page    = 1;
				this.drawer_total   = 0;
				this.drawer_open    = true;
				this.load_drawer_history();
			},

			load_drawer_history() {
				this.drawer_loading = true;
				const api = this.$URL + "/api/v1/technical_records/find/?page=" + this.drawer_page;
				this.$axios.post(api, { deviceId: this.drawer_camera.deviceId }).then(res => {
					if (res && res.data) {
						const items      = this.render_format(res.data.rows || []);
						this.drawer_total = res.data.count || 0;
						if (items) this.drawer_list = [...this.drawer_list, ...items];
					}
					this.drawer_loading = false;
				}).catch(() => { this.drawer_loading = false; });
			},

			load_more_drawer() {
				this.drawer_page++;
				this.load_drawer_history();
			},
		},
	};
</script>
